import { useAppDispatch } from "app/hooks";
import { Loading, Table } from "components";
import { EndUserState } from "helpers";
import React, { useState, useEffect } from "react";
import { UserTableHeadings } from "../UserTableHeadings/UserTableHeadings";
import { UserTableData } from "../UserTableData/UserTableData";
import { useUserManagementContext } from "../utils";
import { UserCommentsModal } from "../UserCommentsModal";
import { UserEditModal } from "../UserEditModal";
import { EndUser, useLazyGetCommentsQuery } from "services";
import { t } from "i18next";

export const UserTable = () => {
  const dispatch = useAppDispatch();
  const isDev = process.env.NODE_ENV === "development";

  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [openComments, setOpenComments] = useState<boolean>(false);
  const [userId, setUserId] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<EndUser | null>(null);
  const { data, isLoading, isFetching, isSuccess, page, setPage } = useUserManagementContext();

  const [
    getComments,
    {
      isFetching: isFetchingComments,
      isSuccess: isSuccessComments,
      isLoading: isLoadingComments,
      isError: isErrorComments,
    },
  ] = useLazyGetCommentsQuery();

  useEffect(() => {
    if (!openEditModal) {
      setSelectedUser(null);
    } else if (selectedUser && data?.clients) {
      const updatedUser = data.clients.find((user) => user.uuid === selectedUser.uuid);
      if (updatedUser) {
        setSelectedUser(updatedUser);
      }
    }
  }, [openEditModal, data, selectedUser]);

  const getDisplayName = (name: string | null, familyName: string | null): string => {
    const firstName = name || "";
    const lastName = familyName || "";

    if (!firstName && !lastName) {
      return t("common.guest");
    }

    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || t("common.guest");
  };

  const endUsers: EndUserState[] =
    data?.clients?.map((user) => ({
      full_name: getDisplayName(user.name, user.family_name),
      first_name: user.first_name || "",
      family_name: user.family_name || "",
      company_name: user.city?.name || "Unknown",
      phone_number: user.phone_number,
      email: user.email,
      last_signin: user.last_login,
      logged_in: user.logged_in,
      status: user.client_state,
      credits: user.credits,
      confirmation_status: user.confirmation_status,
      devices: user.devices,
    })) || [];

  const totalPages = data?.totalPages || 1;

  const onFlag = (endUser: EndUserState) => {
    const actualUser = data?.clients?.find(
      (user) => getDisplayName(user.name, user.family_name) === endUser.full_name && user.email === endUser.email
    );

    if (actualUser) {
      getComments(actualUser.uuid); // Fetch comments instead of notes
      setUserId(actualUser.uuid);
      setOpenComments(true); // Open comments modal
    }
  };

  const onEdit = (endUser: EndUserState) => {
    const actualUser = data?.clients?.find(
      (user) => getDisplayName(user.name, user.family_name) == endUser.full_name && user.email === endUser.email
    );

    if (actualUser) {
      setSelectedUser(actualUser);
      setOpenEditModal(true);
    }
  };

  const onPageChange = (selectedItem: { selected: number }) => {
    setPage(selectedItem.selected);
  };

  return (
    <>
      {isLoading || isFetching ? (
        <Loading />
      ) : !isSuccess ? (
        <p className="text-red-500">{t("messages.loadError")}</p>
      ) : (
        <>
          <Table
            headings={<UserTableHeadings />}
            data={
              <UserTableData endUsers={endUsers} isLoading={isLoading || isFetching} onFlag={onFlag} onEdit={onEdit} />
            }
            page={page}
            pageCount={totalPages}
            onPageChange={onPageChange}
          />
          {openComments && <UserCommentsModal open={openComments} setOpen={setOpenComments} userId={userId} />}
          {openEditModal && <UserEditModal open={openEditModal} setOpen={setOpenEditModal} user={selectedUser} />}
        </>
      )}
    </>
  );
};
export default UserTable;
